import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action } from '@ngrx/store';
import { combineLatest, defer, EMPTY, from, iif, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  throttleTime,
} from 'rxjs/operators';
import { SocketIoService } from 'src/app/shared/socket-io/socket-io.service';

import * as Client from '@vi-iot/client';
import { PaidFeatureScope, UserPropertyEnum, UserRoleType } from '@vi-iot/client';

import { EnvironmentService } from '@services';
import { IAMRoles, InstallationDetailsActions, TutorialsStore } from '@store';
import { AppInsightsMonitoringService } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_FEATURES } from 'src/app/shared/services/app-insights/events/features.events';
import { LogLevelEnum } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';

import { DocumentsStore } from '../documents/documents.store';
import { InvitationsStore } from '../invitations/invitations.store';
import { RemoteConfigActions, RemoteConfigStore } from '../remote-config';
import { UnitsStore } from '../settings/units/units.store';

import { LocalStorageKeys } from '../../shared/models/local-storage-keys.model';
import { isRsa } from '../_common/role-helpers';
import { AuthActions } from './auth.actions';
import { mapUserRoleTypeToGroupType } from './auth.helpers';
import { BUSINESS_ROLES } from './auth.model';
import { AuthStore } from './auth.store';
import { OAuthService } from 'angular-oauth2-oidc';
import { environment } from 'src/environments/environment';
import { AuthHelperService } from './auth-helper.service';
import { IframeCommunication } from 'src/app/shared/services/iframe-communication/iframe-communication.helper';
import { DOCUMENT } from '@angular/common';

@Injectable()
export class AuthEffects {
  private environment = EnvironmentService.getVariables();
  // Auth flow
  // https://wiki.viessmann-climatesolutions.com/pages/viewpage.action?spaceKey=VPL&title=CSRF

  startAuthFlow$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.startAuthFlow),
      switchMap((action) => {
        return defer(() => this.oAuthService.loadDiscoveryDocumentAndTryLogin()).pipe(
          mergeMap(() => {
            this.disconnectLiveUpdates();
            if (this.oAuthService.hasValidAccessToken()) {
              return of(
                AuthActions.getMe.attempt({
                  repeatedAfter401: action.repeatedAfter401,
                  isCSRF: false,
                })
              );
            } else if (
              // Make sure the app uses oAuth
              // If token is invalid but was set previously and we have refresh token - try to refresh it
              !!this.oAuthService.getAccessToken() &&
              !!this.oAuthService.getRefreshToken()
            ) {
              return of(AuthActions.useRefreshToken());
            } else {
              return of(
                AuthActions.getCSRFTokenFromStorage({ repeatedAfter401: action.repeatedAfter401 })
              );
            }
          }),
          catchError(() => of(AuthActions.showLandingPage()))
        );
      })
    );
  });

  // Try to find CSRF token in local storage
  // Function for supporting ViGuide Mobile (angular is embedded within mobile app and token is injected from outside)
  getCSRFTokenFromStorage$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getCSRFTokenFromStorage),
      switchMap((action) => {
        const token = localStorage.getItem(this.environment.csrfTokenConfig.localStorageKey);
        if (token) {
          return of(
            AuthActions.getMe.attempt({ repeatedAfter401: action.repeatedAfter401, isCSRF: true })
          );
        } else {
          return of(AuthActions.showLandingPage());
        }
      })
    );
  });

  // If the app was closed and regular token expired but user has the refresh token
  // Try to create a new token and proceed
  useRefreshToken$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.useRefreshToken),
      switchMap(() =>
        // If user has no refresh token then navigate to landing page directly
        this.oAuthService.getRefreshToken()
          ? defer(() => this.oAuthService.refreshToken()).pipe(
              map(() => AuthActions.startAuthFlow({ repeatedAfter401: true })),
              catchError(() => of(AuthActions.showLandingPage()))
            )
          : of(AuthActions.showLandingPage())
      )
    );
  });

  refreshTokenOn401Error$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.refreshTokenOn401Error),
      switchMap(() =>
        this.oAuthService.getRefreshToken()
          ? defer(this.oAuthService.refreshToken).pipe(
              // In case of forced refresh add this in body block below
              // EMPTY can be used to trick NgRx so it doesn't get error when no actual action is returned
              switchMap(() => {
                this.refreshPage();

                return EMPTY;
              }),
              catchError(() => {
                return of(AuthActions.showLandingPage());
              })
            )
          : of(AuthActions.showLandingPage())
      )
    );
  });

  showLandingPage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.showLandingPage),
        filter(() => {
          const url = this.document.location.href;
          const urlParts = url.split('?')[0].split('/');
          const lastUrlPart = urlParts[urlParts.length - 1];

          // '?' is possible after URL modification with IAM logout
          const ignoredValues = ['home', '?', ''];

          return ignoredValues.every((value) => value !== lastUrlPart);
        }),
        tap(() => {
          this.oAuthService.postLogoutRedirectUri = '';
          this.oAuthService.logoutUrl = '';
          this.oAuthService.logOut();
          this.clearLocalStorage();
          this.disconnectLiveUpdates();
          this.saveTargetUrlBeforeLandingPageRedirection();
          this.router.navigate(['/home']);
        })
      );
    },
    { dispatch: false }
  );

  // Get logged in user data (/me)
  getMe$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getMe.attempt),
      tap(() => {
        this.appInsights.logEvent(APP_INSIGHTS_FEATURES.GET_ME, LogLevelEnum.TRACE);
      }),
      switchMap((action) =>
        this.userRegistrationApi.getLoggedInUserDetails().pipe(
          map((me) => AuthActions.getMe.success({ me })),
          catchError((error) => {
            this.appInsights.logEvent(APP_INSIGHTS_FEATURES.GET_ME_ERROR, LogLevelEnum.ERROR, {
              error,
            });

            return of(
              AuthActions.getMe.error({
                statusCode: error?.statusCode,
                repeatedAfter401: action.repeatedAfter401,
                isCSRF: action.isCSRF,
              })
            );
          })
        )
      )
    );
  });

  // User is authenticated here.
  // Get some app data like
  // settings, remote config, pending invitations
  getMeSuccess$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.getMe.success),
        tap((action) => {
          this.fetchData(action.me);
          this.tutorialsStore.fetchTutorials();
        })
      );
    },
    { dispatch: false }
  );

  // User is authenticated here.
  // authorize user (maintainer or RSA)
  getRemoteConfigSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteConfigActions.getRemoteConfigSuccess),
      switchMap((action) =>
        combineLatest([
          of(action),
          this.authStore.state$.pipe(
            filter((state) => !!state.me),
            take(1)
          ),
        ])
      ),
      switchMap(([_action, state]) => {
        const userCountryCode: string = state?.me?.address?.countryCode || '';
        this.documentsStore.getDocumentsUpdated(userCountryCode);
        const roleGroup = this.roleGroup(state.me);
        const businessRoles = state.me.businessRoles;
        const identityType = state.me.properties.find(
          (property) => property.name === UserPropertyEnum.IdentityType
        )?.value as IAMRoles;

        return of(AuthActions.authorizeMe({ roleGroup, businessRoles, identityType }));
      })
    );
  });

  // Side effect for failed /me endpoint (fetching logged in user data)
  getMeError$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getMe.error),
      map((action) => {
        // if statusCode is 401 then we refetch token
        if (action.repeatedAfter401) {
          return AuthActions.showLandingPage();
        } else if (action.statusCode === 401 && !action.isCSRF) {
          return AuthActions.useRefreshToken();
        } else {
          return AuthActions.navigateToForbiddenPage();
        }
      })
    );
  });

  navigateToForbiddenPage$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.navigateToForbiddenPage),
        tap(() => {
          this.router.navigate(['/access-forbidden']);
        })
      );
    },
    { dispatch: false }
  );

  // Authorization part. Check the user role type and depends on it navigate to the right page
  authorizeMe$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.authorizeMe),
        concatLatestFrom(() => [
          this.remoteConfigStore.remoteConfig$,
          this.authStore.isRequiredApplicationRole$,
          this.authStore.isUserNavigateToHeatingSystemsOverviewExternal$,
        ]),
        switchMap(
          ([
            action,
            remoteConfig,
            isRequiredApplicationRole,
            isUserShouldBeRedirectedToHeatingSystemsOverview,
          ]) => {
            const isRsaUser = isRsa(action.roleGroup);

            const isLiteAccount = action.businessRoles?.some(
              (role) => role === BUSINESS_ROLES.DEFAULT_LITE_ACCOUNT
            );
            const isEndCustomerBlocked =
              action.identityType === IAMRoles.endCustomer && remoteConfig?.blockEndCustomers;

            let redirectUrl = this.router.url;
            if (this.route.snapshot.fragment) {
              redirectUrl = this.router.url.replace(this.route.snapshot.fragment, '').slice(0, -1);
            }

            if (isUserShouldBeRedirectedToHeatingSystemsOverview) {
              this.document.location.href = this.environment.heatingSystemsUrl;
            }

            if (isLiteAccount || isEndCustomerBlocked || !isRequiredApplicationRole) {
              this.router.navigate(['/access-forbidden']);
            } else {
              if (redirectUrl.includes('/home') || redirectUrl.includes('/access-forbidden')) {
                const url = isRsaUser ? '/search' : '/dashboard';

                this.router.navigate([url], {
                  queryParams: {
                    ...this.route.snapshot.queryParams,
                    sessionid: null,
                    timestampQueryParam: null,
                    code: null,
                    state: null,
                  },
                  queryParamsHandling: 'merge',
                });
              }
            }

            return of(action);
          }
        )
      );
    },
    { dispatch: false }
  );

  // Side effect for logout action. Disconnect Live Updates & clear local storage
  logout$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.logout),
        tap(() => {
          this.oAuthService.postLogoutRedirectUri = environment.authConfig.logoutRedirectUri;
          this.oAuthService.logoutUrl = environment.oAuthConfig.logoutUrl;
          this.oAuthService.logOut();
          this.clearLocalStorage();
          this.disconnectLiveUpdates();
        })
      );
    },
    { dispatch: false }
  );

  runDemo$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.runDemo),
      switchMap(() => {
        sessionStorage.setItem(LocalStorageKeys.isDemoModeRunning, 'true');

        return of(AuthActions.getMe.attempt({ repeatedAfter401: false, isCSRF: false }));
      })
    );
  });

  exitDemo$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.exitDemo),
        tap(() => {
          sessionStorage.removeItem(LocalStorageKeys.isDemoModeRunning);

          this.router.navigate(['/home']);
        })
      );
    },
    { dispatch: false }
  );

  showEventBanner$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.eventBanner.showAttempt),
      switchMap(() => {
        const isBannerActive = localStorage.getItem('newVitoguideFeaturesBanner');

        return of(AuthActions.eventBanner.showSuccess({ isActive: isBannerActive !== 'false' }));
      })
    );
  });

  closeEventBanner$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.eventBanner.closeAttempt),
      switchMap(() => {
        localStorage.setItem('newVitoguideFeaturesBanner', 'false');

        return of(AuthActions.eventBanner.closeSuccess());
      })
    );
  });

  showPaidFeaturesBanner$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.paidFeaturesBanner.showAttempt),
      switchMap(() => {
        const isBannerActive = localStorage.getItem('paidFeaturesBanner');

        return of(
          AuthActions.paidFeaturesBanner.showSuccess({ isActive: isBannerActive !== 'false' })
        );
      })
    );
  });

  closePaidFeaturesBanner$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.paidFeaturesBanner.closeAttempt),
      switchMap(() => {
        localStorage.setItem('paidFeaturesBanner', 'false');

        return of(AuthActions.paidFeaturesBanner.closeSuccess());
      })
    );
  });

  showLearningRoomBanner$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.learningRoomBanner.showAttempt),
      switchMap(() => {
        const isBannerActive = localStorage.getItem('learningRoomBanner');

        return of(
          AuthActions.learningRoomBanner.showSuccess({ isActive: isBannerActive !== 'false' })
        );
      })
    );
  });

  closeLearningRoomBanner$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.learningRoomBanner.closeAttempt),
      switchMap(() => {
        localStorage.setItem('learningRoomBanner', 'false');

        return of(AuthActions.learningRoomBanner.closeSuccess());
      })
    );
  });

  readCompanyPaidFeatures$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getMe.success, InstallationDetailsActions.ReadInstallationDetailsSuccess),
      concatLatestFrom(() => [this.authStore.me$, this.authStore.isRsaUser()]),
      filter(([_action, _authMeState, isRsaUser]) => !isRsaUser && !!_authMeState.companyId),
      switchMap(([_action, authMeState]) => {
        return this.purchasesApi
          .getAvailablePaidFeaturesForSingleInstallation(
            PaidFeatureScope.company,
            authMeState.companyId
          )
          .pipe(
            map((deviceTable) => deviceTable[0]),
            map((subscriptionWithPaidFeatures) =>
              AuthActions.readCompanyPaidFeatures.success({
                subscriptionWithPaidFeatures,
              })
            ),
            catchError(() => of(AuthActions.readCompanyPaidFeatures.error()))
          );
      })
    );
  });

  getConnectivityRanking$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getMe.success),
      switchMap(() =>
        this.authStore.getCompanyId().pipe(
          filter((id) => !!id),
          take(1)
        )
      ),
      filter((companyId) => companyId.length === 9 || companyId.length === 10),
      switchMap((companyId: string) =>
        this.analyticsApiPicker
          .instance()
          .getConnectivityRanking(companyId)
          .pipe(
            map((response) =>
              AuthActions.getConnectivityRanking.success({ connectivityRanking: response.data })
            ),
            catchError(() => of(AuthActions.getConnectivityRanking.error()))
          )
      )
    );
  });

  private disconnectLiveUpdates(): void {
    this.socketIoService.disconnect();
  }

  private roleGroup(me: Client.LoggedInUserDetails): Client.RoleGroupType {
    const role: UserRoleType = me.iotRole;

    return mapUserRoleTypeToGroupType(role);
  }

  private fetchData(clientData: Client.LoggedInUserDetails): void {
    if (this.isUserRoleAMaintainer(clientData.iotRole)) {
      this.invitationsStore.getPendingInvitationsCount();
    }

    this.remoteConfigStore.getRemoteConfig(this.environment.appId);
    this.unitStore.fetchTemperatureUnit();
  }

  private isUserRoleAMaintainer(role: UserRoleType): boolean {
    switch (role) {
      case UserRoleType.maintainer:
      case UserRoleType.maintainerDeputy:
      case UserRoleType.maintainerAdmin:
      case UserRoleType.maintainerCEO:
        return true;
      default:
        return false;
    }
  }

  private clearLocalStorage(): void {
    Object.values(LocalStorageKeys).forEach((localStorageKey) =>
      localStorage.removeItem(localStorageKey)
    );
    localStorage.removeItem(this.environment.csrfTokenConfig.localStorageKey);
  }

  constructor(
    private userRegistrationApi: Client.UserRegistrationApi,
    private actions$: Actions,
    private remoteConfigStore: RemoteConfigStore,
    private authStore: AuthStore,
    private unitStore: UnitsStore,
    private invitationsStore: InvitationsStore,
    private documentsStore: DocumentsStore,
    private router: Router,
    private route: ActivatedRoute,
    private socketIoService: SocketIoService,
    private appInsights: AppInsightsMonitoringService,
    private purchasesApi: Client.PurchasesApi,
    private analyticsApiPicker: Client.AnalyticsApiPicker,
    private tutorialsStore: TutorialsStore,
    private oAuthService: OAuthService,
    private authHelperService: AuthHelperService,
    private companyApi: Client.CompanyApi,
    @Inject(DOCUMENT) private document: Document
  ) {
    if (environment.oAuthConfig) {
      this.oAuthService.configure(environment.oAuthConfig);
      this.oAuthService.setupAutomaticSilentRefresh();
    }
  }

  logIn$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.authenticateMe.attempt),
      mergeMap(() =>
        iif(
          () => this.oAuthService.hasValidIdToken() && this.oAuthService.hasValidAccessToken(),
          [AuthActions.authenticateMe.success()],
          from(this.oAuthService.loadDiscoveryDocumentAndLogin()).pipe(
            tap((result: boolean): void => {
              if (!result) {
                this.oAuthService.initCodeFlow();
              }
            }),
            mergeMap(() => EMPTY),
            catchError((error) => [AuthActions.authenticateMe.error(error)])
          )
        )
      )
    );
  });

  logInSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.authenticateMe.success),
      switchMap(() => of(AuthActions.getMe.attempt({ repeatedAfter401: false, isCSRF: false })))
    );
  });

  logInError$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.authenticateMe.error),
      switchMap(() => of(AuthActions.navigateToForbiddenPage()))
    );
  });

  iframeRefreshToken$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.iframeRefreshToken),
        throttleTime(2000),
        mergeMap(() => defer(() => this.oAuthService.refreshToken())),
        tap(() => {
          this.authHelperService.iframeRefreshToken$.next(
            IframeCommunication.MessageName.NEW_TOKEN
          );
        }),
        catchError(() => {
          // Refresh token failed which means no action can be done
          // Try to start 401 as during regular error and redirect to homepage if impossible to proceed
          this.authStore.startAuthFlow();

          return of(null);
        })
      );
    },
    { dispatch: false }
  );

  attemptGetUserCompany$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.authorizeMe),
      concatLatestFrom(() => [this.authStore.isMaintainer$]),
      filter(([_action, isMaintaier]) => isMaintaier),
      map(() => AuthActions.getUserCompany.attempt())
    );
  });

  getUserCompany$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getUserCompany.attempt),
      concatLatestFrom(() => [this.authStore.getMe()]),
      switchMap(([_action, me]) =>
        this.companyApi.getUserCompany(me.companyId).pipe(
          map((data) => AuthActions.getUserCompany.success({ data })),
          catchError((error) => of(AuthActions.getUserCompany.error({ error })))
        )
      )
    );
  });

  navigateToTargetUrl$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.authorizeMe),
        tap(() => {
          const targetUrl = localStorage.getItem(LocalStorageKeys.TargetRedirectionUrl);
          localStorage.removeItem(LocalStorageKeys.TargetRedirectionUrl);
          if (targetUrl) {
            this.router.navigateByUrl(targetUrl);
          }
        })
      );
    },
    { dispatch: false }
  );

  /**
   * Method created due to issues with unit tests
   */
  private refreshPage(): void {
    location.reload();
  }

  private saveTargetUrlBeforeLandingPageRedirection(): void {
    const targetUrl = location.href.split(location.origin)[1];
    if (targetUrl) {
      localStorage.setItem(LocalStorageKeys.TargetRedirectionUrl, targetUrl);
    }
  }
}
